import type {
  InventoryUnit,
  Location,
  PrinterStatus,
  PrintResult,
  SystemInformation,
  User
} from "./types";

let csrfToken = sessionStorage.getItem("parallax-csrf") || "";

export class ApiError extends Error {
  status: number;
  detail: string;

  constructor(status: number, detail: string) {
    super(detail);
    this.status = status;
    this.detail = detail;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (!(options.body instanceof FormData) && options.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  if (options.method && !["GET", "HEAD"].includes(options.method.toUpperCase()) && csrfToken) {
    headers.set("X-CSRF-Token", csrfToken);
  }
  const response = await fetch(path, { ...options, headers, credentials: "same-origin" });
  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      detail = typeof body.detail === "string" ? body.detail : JSON.stringify(body.detail);
    } catch {
      // The status text is still useful for non-JSON failures.
    }
    throw new ApiError(response.status, detail);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export function setCsrf(token: string) {
  csrfToken = token;
  sessionStorage.setItem("parallax-csrf", token);
}

export const api = {
  async login(
    username: string,
    password: string,
    trusted: boolean,
    deviceLabel: string
  ): Promise<User> {
    const result = await request<{ user: User; csrf_token: string }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({
        username,
        password,
        trusted,
        device_label: deviceLabel || null
      })
    });
    setCsrf(result.csrf_token);
    return result.user;
  },
  async me(): Promise<User> {
    const result = await request<{ user: User; csrf_token: string }>("/api/auth/me");
    setCsrf(result.csrf_token);
    return result.user;
  },
  logout: () => request("/api/auth/logout", { method: "POST" }),
  dashboard: () => request("/api/dashboard"),
  listInventory: (query = "") =>
    request<InventoryUnit[]>(`/api/inventory${query ? `?q=${encodeURIComponent(query)}` : ""}`),
  getInventory: (id: string) => request<InventoryUnit>(`/api/inventory/${id}`),
  createInventory: (payload: object) =>
    request<InventoryUnit>("/api/inventory", { method: "POST", body: JSON.stringify(payload) }),
  updateInventory: (id: string, payload: object) =>
    request<InventoryUnit>(`/api/inventory/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload)
    }),
  updateStatus: (id: string, status: string, version: number) =>
    request<InventoryUnit>(`/api/inventory/${id}/status`, {
      method: "POST",
      body: JSON.stringify({ status, version })
    }),
  updateLocation: (id: string, locationId: string, version: number) =>
    request<InventoryUnit>(`/api/inventory/${id}/location`, {
      method: "POST",
      body: JSON.stringify({ location_id: locationId, version })
    }),
  addItem: (id: string, payload: object) =>
    request(`/api/inventory/${id}/items`, {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  rapidPreview: (id: string, text: string) =>
    request<Array<{ source_line: string; item_name: string; quantity: string; unit_of_measure: string; confidence: string }>>(
      `/api/inventory/${id}/rapid/preview`,
      { method: "POST", body: JSON.stringify({ text }) }
    ),
  rapidSave: (id: string, items: object[]) =>
    request(`/api/inventory/${id}/rapid`, { method: "POST", body: JSON.stringify(items) }),
  locations: () => request<Location[]>("/api/locations"),
  resolveTag: (token: string) => request<Record<string, unknown>>(`/api/tags/resolve/${token}`),
  provisionTag: () => request<TagProvision>("/api/tags", { method: "POST" }),
  assignTag: (token: string, inventoryUnitId: string) =>
    request(`/api/tags/${token}/assign`, {
      method: "POST",
      body: JSON.stringify({ inventory_unit_id: inventoryUnitId })
    }),
  updateTagStatus: (tagId: string, status: string, note?: string) =>
    request(`/api/tags/id/${tagId}/status`, {
      method: "POST",
      body: JSON.stringify({ status, note: note || null })
    }),
  getInventoryNfc: (unitId: string) =>
    request<TagProvision[]>(`/api/inventory/${unitId}/nfc-tags`),
  createInventoryNfc: (unitId: string) =>
    request<TagProvision>(`/api/inventory/${unitId}/nfc-tag`, { method: "POST" }),
  runExport: () => request<{ ok: boolean; path: string }>("/api/exports/run", { method: "POST" }),
  runBackup: () => request<{ ok: boolean; path: string }>("/api/backups", { method: "POST" }),
  system: () => request<SystemInformation>("/api/system"),
  updateBaseUrl: (baseUrl: string) =>
    request<{ ok: boolean; base_url: string; restart_recommended: boolean }>("/api/system/base-url", {
      method: "PATCH",
      body: JSON.stringify({ base_url: baseUrl })
    }),
  printerStatus: () => request<PrinterStatus>("/api/printer/status"),
  updatePrinterConfiguration: (
    target: string | null,
    confirmGenericBluetooth = false
  ) =>
    request<{ ok: boolean; target: string | null }>("/api/printer/configuration", {
      method: "PATCH",
      body: JSON.stringify({
        target,
        confirm_generic_bluetooth: confirmGenericBluetooth
      })
    }),
  printInventoryLabel: (unitId: string, copies = 1) =>
    request<PrintResult>(`/api/printer/inventory/${unitId}/print`, {
      method: "POST",
      body: JSON.stringify({ copies })
    }),
  printTestLabel: (copies = 1) =>
    request<{ ok: boolean; status: "sent"; port: string; message: string }>(
      "/api/printer/test",
      {
        method: "POST",
        body: JSON.stringify({ copies })
      }
    ),
  createBatch: (destinationLocationId: string) =>
    request<BatchResult>("/api/batches", {
      method: "POST",
      body: JSON.stringify({ destination_location_id: destinationLocationId })
    }),
  getBatch: (token: string) => request<BatchResult>(`/api/batches/${token}`),
  getCurrentBatch: () => request<BatchResult>("/api/current-batch"),
  confirmBatch: (token: string) =>
    request<{ ok: boolean; updated: number }>(`/api/batches/${token}/confirm`, {
      method: "POST"
    }),
  confirmCurrentBatch: () =>
    request<{ ok: boolean; updated: number }>("/api/current-batch/confirm", {
      method: "POST"
    }),
  removeBatchEntry: (token: string, entryId: string) =>
    request<BatchResult>(`/api/batches/${token}/entries/${entryId}`, { method: "DELETE" }),
  removeCurrentBatchEntry: (entryId: string) =>
    request<BatchResult>(`/api/current-batch/entries/${entryId}`, { method: "DELETE" })
};

export interface TagProvision {
  id: string;
  token: string;
  token_preview: string;
  url: string;
  status: string;
}

export interface BatchResult {
  id: string;
  token?: string;
  status: string;
  count: number;
  destination: Location;
  entries: Array<{
    id: string;
    inventory_unit_id: string;
    stock_number: string;
    name: string;
    current_location: string | null;
  }>;
}

export function uploadPhotos(
  unitId: string,
  files: FileList | File[],
  category: string,
  onProgress: (value: number) => void
): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const body = new FormData();
    Array.from(files).forEach((file) => body.append("files", file));
    body.append("category", category);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/inventory/${unitId}/photos`);
    xhr.withCredentials = true;
    if (csrfToken) xhr.setRequestHeader("X-CSRF-Token", csrfToken);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText));
      } else {
        let detail = "Upload failed";
        try {
          detail = JSON.parse(xhr.responseText).detail || detail;
        } catch {
          // Keep the generic failure.
        }
        reject(new ApiError(xhr.status, detail));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, "Network interrupted. Check Wi-Fi and retry."));
    xhr.send(body);
  });
}
